import { Button, Flex, Text, Textarea, useDisclosure } from "@chakra-ui/react";
import { Field, FieldProps, Form, Formik } from "formik";
import React from "react";
import { RiEditLine } from "react-icons/ri";
import { CustomFormInput } from "src/components/Inputs/CustomFormInput";
import { Card } from "src/components/Layout/Card";
import { ResizableDrawer } from "src/components/Layout/ResizableDrawer";
import {
  NavigationBlocker,
  NavigationBlockerProvider,
} from "src/components/NavigationBlocker";
import { useFormTemplateContext } from "src/scenes/orgAdmin/formTemplates/formTemplateContext";
import { validateWithZod } from "src/services/formValidations";
import * as AF from "src/types/formTemplate";
import { z } from "zod";
import { DirtyFormTracker } from "../QuestionForm/DirtyFormTracker";

type Props = {
  section: AF.V3.DisclaimerSection;
  onUpdate?: (disclaimer: AF.V3.DisclaimerSection) => void;
};
export const DisclaimerForm: React.FC<Props> = ({ section, onUpdate }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const onUpdateButtonClick = React.useCallback(
    (updatedDisclaimer: AF.V3.DisclaimerSection) => {
      if (onUpdate) onUpdate(updatedDisclaimer);
      onClose();
    },
    [onClose, onUpdate]
  );

  return (
    <Card display="flex" flexDirection="column" showBorder gap="3">
      <DisclaimerFormEditDrawer
        isOpen={isOpen}
        disclaimer={section}
        onClose={onClose}
        onUpdate={onUpdateButtonClick}
      />
      <DisclaimerFormView disclaimer={section} onEditButtonClick={onOpen} />
    </Card>
  );
};

type ViewProps = {
  disclaimer: AF.V3.DisclaimerSection;
  onEditButtonClick: () => void;
};
export const DisclaimerFormView: React.FC<ViewProps> = ({
  disclaimer,
  onEditButtonClick,
}) => {
  const { mode } = useFormTemplateContext();
  return (
    <Flex direction="column" gap="3">
      <Flex justifyContent="flex-end" alignItems="center">
        {mode === "edit" ? (
          <Button
            colorScheme="gray"
            variant="outline"
            leftIcon={<RiEditLine />}
            flexShrink={0}
            onClick={onEditButtonClick}
          >
            Edit disclaimer
          </Button>
        ) : null}
      </Flex>
      <Text fontWeight="bold">Disclaimer Text</Text>
      <Text fontSize="sm" whiteSpace="pre-wrap">
        {disclaimer.disclaimer}
      </Text>
    </Flex>
  );
};

const FormSchema = z.object({
  disclaimer: z.string().min(1, "This field is required"),
});
type FormType = z.infer<typeof FormSchema>;
type EditProps = {
  isOpen: boolean;
  disclaimer: AF.V3.DisclaimerSection;
  onClose: () => void;
  onUpdate: (question: AF.V3.DisclaimerSection) => void;
};
export const DisclaimerFormEditDrawer: React.FC<EditProps> = ({
  isOpen,
  disclaimer,
  onClose,
  onUpdate,
}) => {
  const onSubmit = React.useCallback(
    (values: FormType) => {
      onUpdate({ ...disclaimer, disclaimer: values.disclaimer });
    },
    [disclaimer, onUpdate]
  );

  return (
    <NavigationBlockerProvider>
      {({ confirm, hasChanges }) => {
        const onCancelHandler = async () => {
          if (!hasChanges || (await confirm())) {
            onClose();
          }
        };

        return (
          <ResizableDrawer isOpen={isOpen} onClose={onCancelHandler}>
            <Formik<FormType>
              initialValues={{
                disclaimer: disclaimer.disclaimer,
              }}
              onSubmit={onSubmit}
              validate={validateWithZod(FormSchema)}
            >
              {(formik) => {
                return (
                  <ResizableDrawer.Content as={Form} noValidate>
                    <NavigationBlocker hasChanges={formik.dirty} />
                    <ResizableDrawer.Header>
                      Edit disclaimer
                    </ResizableDrawer.Header>
                    <ResizableDrawer.Body>
                      <Flex direction="column" gap="3">
                        <DirtyFormTracker />
                        <Field name="disclaimer">
                          {({ field, meta }: FieldProps) => (
                            <CustomFormInput
                              label="Disclaimer Text"
                              error={meta.error}
                            >
                              <Textarea height="20rem" {...field} />
                            </CustomFormInput>
                          )}
                        </Field>
                      </Flex>
                    </ResizableDrawer.Body>
                    <ResizableDrawer.Footer>
                      <Button
                        variant="outline"
                        colorScheme="gray"
                        onClick={onCancelHandler}
                      >
                        Cancel
                      </Button>
                      <Button type="submit">Update disclaimer</Button>
                    </ResizableDrawer.Footer>
                  </ResizableDrawer.Content>
                );
              }}
            </Formik>
          </ResizableDrawer>
        );
      }}
    </NavigationBlockerProvider>
  );
};
